<template>
  <div class="editor-sidebar-wrapper" @keydown.esc="closeSnippets">
    <!-- Floating button to show sidebar when hidden -->
    <button
      v-if="!isVisible"
      class="btn-show-sidebar"
      @click="showSidebar"
    >
      <b-icon icon="plus-lg" />
    </button>

    <!-- Main Sidebar -->
    <div
      v-if="isVisible"
      class="editor-sidebar"
      :class="{ 'sidebar-open': isOpen }"
    >
      <!-- One column with the categories only (2026-09-29): hovering or
           clicking a category opens its snippets in a flyout to the left,
           floating over the canvas. The sidebar keeps its width, so the
           canvas never has to move. -->
      <div class="categories-panel">
        <!-- Snippets / Layout tabs with close button -->
        <div v-if="isVue3Mode" class="sidebar-tabs">
          <button
            class="sidebar-tab"
            :class="{ active: activeTab === 'snippets' }"
            @click="switchTab('snippets')"
          >
            {{ $t('editor.snippets_tab') }}
          </button>
          <!-- Gone for pilot groups ("Neues Snippet-System"): the new
               containers do what the page layout picker did. -->
          <button
            v-if="showLayoutTab"
            class="sidebar-tab"
            :class="{ active: activeTab === 'layout' }"
            @click="switchTab('layout')"
          >
            {{ $t('editor.layout_tab') }}
          </button>
          <button class="btn-icon btn-hide-tabs" @click="hideSidebar">
            <b-icon icon="x" />
          </button>
        </div>
        <!-- Fallback header for non-Vue3 mode -->
        <div v-else class="categories-header">
          <span class="header-title">{{ $t('editor.sidebar.sections') }}</span>
          <button class="btn-icon btn-hide" @click="hideSidebar">
            <b-icon icon="x" />
          </button>
        </div>
        <!-- Snippets tab content: search + category list -->
        <template v-if="activeTab === 'snippets'">
          <div class="category-search">
            <b-icon icon="search" class="category-search-icon" />
            <input
              v-model.trim="searchQuery"
              type="text"
              class="category-search-input"
              :placeholder="$t('webmag.search')"
            />
            <button
              v-if="searchQuery"
              class="category-search-clear"
              @click="searchQuery = ''"
            >
              <b-icon icon="x" />
            </button>
          </div>
          <ul class="category-list" @mouseleave="leaveCategories">
            <li
              v-for="(category, index) in sortedCategories"
              :key="`${category || 'category'}-${index}`"
              :class="{ selected: showSnippets && !searchQuery && selectedCategory === category }"
              tabindex="0"
              @mouseenter="hoverCategory(category)"
              @click="selectCategory(category)"
              @keydown.enter.prevent="selectCategory(category)"
            >
              <b-icon icon="chevron-left" class="chevron" />
              {{ getCategoryLabel(category) }}
            </li>
          </ul>
        </template>
        <!-- Layout tab content: layout picker inline -->
        <template v-else-if="activeTab === 'layout'">
          <div class="layouts-list">
            <div
              v-for="group in layoutGroups"
              :key="group.label"
              class="layout-group"
            >
              <h4 class="layout-group-label">{{ uiLabel('layoutGroups', group.label) }}</h4>
              <div class="layout-group-grid">
                <div
                  v-for="layout in group.layouts"
                  :key="layout.style"
                  class="layout-card"
                  :class="{ active: currentLayout === layout.style }"
                  @click="selectLayout(layout.style)"
                >
                  <div class="layout-preview">
                    <div
                      class="layout-preview-bar layout-preview-left"
                      :style="getLayoutPreviewStyle(layout.style, 'left')"
                    ></div>
                    <div
                      class="layout-preview-bar layout-preview-right"
                      :style="getLayoutPreviewStyle(layout.style, 'right')"
                    ></div>
                  </div>
                  <span class="layout-label">{{ uiLabel('layouts', layout.style, layout.label) }}</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Second level: the snippets of the hovered/clicked category, or the
         search results, floating over the canvas left of the sidebar.
         Hidden (not closed) during a drag so the whole canvas is a drop zone. -->
    <div
      v-if="isVisible && showSnippets && activeTab === 'snippets'"
      v-show="!isDraggingSnippet"
      class="snippets-flyout"
      @mouseenter="keepFlyoutOpen"
      @mouseleave="scheduleFlyoutClose"
    >
      <div class="snippets-header">
        <span class="header-title">{{ snippetsTitle }}</span>
        <button
          class="btn-icon btn-close-snippets"
          :title="$t('editor.sidebar.close')"
          @click="closeSnippets"
        >
          <b-icon icon="x" />
        </button>
      </div>
      <div class="snippets-list">
        <div
          v-for="(snippet, index) in listedSnippets"
          :key="`${snippet.name || snippet.title || snippet.preview || 'snippet'}-${index}`"
          class="snippet-card"
          tabindex="0"
          :title="snippet.beschreibung || null"
          @mousedown="startSnippetDrag(snippet, $event)"
          @keydown.enter.self.prevent="addSnippet(snippet)"
          @dragstart.prevent
          draggable="false"
        >
          <button
            v-if="snippet.snippetId"
            type="button"
            class="snippet-favorite-button"
            :class="{ 'is-favorite': snippet.isFavorite }"
            :title="snippet.isFavorite
              ? $t('editor.sidebar.favoriteRemove')
              : $t('editor.sidebar.favoriteAdd')"
            @click.stop="$emit('toggle-snippet-favorite', snippet)"
            @mousedown.stop
            draggable="false"
          >
            <b-icon :icon="snippet.isFavorite ? 'star-fill' : 'star'" />
          </button>
          <button
            type="button"
            class="snippet-add-button"
            @click.stop="addSnippet(snippet)"
            @mousedown.stop
            draggable="false"
          >
            <b-icon icon="plus-lg" />
          </button>
          <div class="snippet-preview">
            <!-- v2 rows are drawn live from their own markup, with the
                 magazine's CSS and the customer's CI (2026-09-22). The
                 puzzle stays as the fallback while nothing can be drawn. -->
            <ContainerSketch
              v-if="skizze(snippet)"
              :sketch="skizze(snippet)"
            />
            <SnippetLivePreview
              v-else-if="isV2Markup(snippet.content || snippet.html)"
              :html="snippet.content || snippet.html"
              :locale="language"
            >
              <div class="snippet-placeholder">
                <b-icon icon="puzzle" />
              </div>
            </SnippetLivePreview>
            <img
              v-else-if="snippet.preview"
              :src="getPreviewUrl(snippet.preview)"
              :alt="getSnippetTitle(snippet)"
              draggable="false"
              @dragstart.prevent
              @error="handleImageError"
            />
            <div v-else class="snippet-placeholder">
              <b-icon icon="puzzle" />
            </div>
          </div>
          <span class="snippet-name">
            <b-icon v-if="snippet.icon" :icon="snippet.icon" class="snippet-icon" />
            {{ getSnippetTitle(snippet) }}
            <!-- A row that belongs to this customer alone. -->
            <span v-if="snippet.groupId != null" class="snippet-own">{{ $t('editor.ownRow') }}</span>
            <span v-if="snippet.vorlage && snippet.vorlage.beispiel" class="snippet-own">{{ $t('editor.vorlagen.beispiel') }}</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BIcon } from 'bootstrap-vue';
import { LAYOUT_GROUPS } from '@/editor-v2/utils/layoutWrapper';
import { FAVORITES_CATEGORY, filterSnippetsByFavorites } from '@/utils/dbSnippetMapper';
import { getSnippetCategoryLabel } from '@/utils/snippetCategories';
import { isMobileViewport } from '@/utils/mobileViewport';
import { readLevel } from '@/components/editor/controls/panel/panelLevel';
import { bereichSketch, containerSketch, isV2Markup } from '@/editor-v2/utils/livePreviewFrames';
import ContainerSketch from './ContainerSketch.vue';
import SnippetLivePreview from './SnippetLivePreview.vue';

/** What the column draws and what the canvas leaves free: one number. */
const SIDEBAR_WIDTH = 320;
/** Hover intent before a category opens its flyout, so passing over does not flicker. */
const HOVER_OPEN_MS = 120;
/** Grace time after the pointer leaves categories and flyout (also covers moving into the canvas iframe). */
const HOVER_CLOSE_MS = 300;

export default {
  name: 'EditorSidebar',
  components: {
    BIcon,
    ContainerSketch,
    SnippetLivePreview,
  },
  props: {
    snippetData: {
      type: Object,
      default: () => ({
        containers: [],
        components: [],
        categories: [],
      }),
    },
    language: {
      type: String,
      default: 'de',
    },
    isVue3Mode: {
      type: Boolean,
      default: false,
    },
    showLayoutTab: {
      type: Boolean,
      default: true,
    },
    isSysAdmin: {
      type: Boolean,
      default: false,
    },
    templateBaseUrl: {
      type: String,
      default: '',
    },
    currentLayout: {
      type: String,
      default: '100',
    },
    // For the per-user panel level (Einfach/Profi): "Eigener Bereich" is Profi only.
    userId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      isOpen: true,
      // Phone-sized host: start closed so the canvas gets the full width;
      // the floating "+" button opens the sidebar as a full-width overlay.
      isVisible: !isMobileViewport(),
      showSnippets: false,
      panelLevel: 'simple',
      activeTab: 'snippets',
      searchQuery: '',
      selectedCategory: null,
      layoutGroups: LAYOUT_GROUPS,
      dragCandidate: null,
      dragOverlay: null,
      dragSnippet: null,
      dragSnippetIsContainer: false,
      isDraggingSnippet: false,
      lastDragPosition: null,
      dragMoveHandler: null,
      dragUpHandler: null,
      dragGhost: null,
      dragIframe: null,
      dragIframeDoc: null,
      dragIframeMoveHandler: null,
      dragIframeUpHandler: null,
      iframePointerEventsDisabled: false,
      // Flyout opened by the "+" between snippets (KEditor.snippetKatalogOeffnen):
      // stays open on mouseleave until a snippet is picked or the catalog closes.
      flyoutPinned: false,
      // Flyout opened by typing a search: clearing the search closes it again.
      searchOpenedFlyout: false,
      hoverOpenTimer: null,
      hoverCloseTimer: null,
    };
  },
  computed: {
    sortedCategories() {
      // "Alle" first: every snippet of every category (listedSnippets skips the filter for 'All').
      const categories = (this.snippetData.categories || []).filter((c) => c !== 'All');
      const hasFavorites = filterSnippetsByFavorites(this.snippetData.components || []).length > 0;
      return ['All', ...(hasFavorites ? [FAVORITES_CATEGORY] : []), ...categories];
    },
    snippetsTitle() {
      const query = this.searchQuery.trim();
      if (query) {
        return `${this.$t('webmag.search')}: ${query}`;
      }
      return this.getCategoryLabel(this.selectedCategory);
    },
    listedSnippets() {
      let snippets = (this.snippetData.components || []).filter(
        (snippet) => snippet && typeof snippet === 'object',
      );

      // Hide element-only snippets (data-wps-element="true") from sidebar
      snippets = snippets.filter((s) => !s.isElementOnly);
      // "Eigener Bereich" is for Profi only (spec 2026-09-23 §2.1).
      snippets = snippets.filter((s) => !s.nurProfi || this.panelLevel === 'pro');

      const query = this.normalizeSearchText(this.searchQuery);

      if (!query && this.selectedCategory === FAVORITES_CATEGORY) {
        return filterSnippetsByFavorites(snippets);
      }

      // Filter by category
      if (!query && this.selectedCategory && this.selectedCategory !== 'All') {
        snippets = snippets.filter(
          (s) => s.categories && s.categories.includes(this.selectedCategory),
        );
      }

      // Filter by search query
      if (query) {
        snippets = snippets.filter((s) => {
          const searchableText = this.getSnippetSearchableText(s);
          return searchableText.includes(query);
        });
      }

      // Catalog order (feedback 28.09.2026): rows with a place first, by place; the rest keep their order.
      return snippets
        .map((s, i) => ({ s, i }))
        .sort((a, b) => (a.s.sort ?? Infinity) - (b.s.sort ?? Infinity) || a.i - b.i)
        .map(({ s }) => s);
    },
  },
  watch: {
    searchQuery() {
      this.refreshPanelLevel();
      const hasQuery = this.searchQuery.trim().length > 0;
      if (hasQuery && !this.showSnippets) {
        this.showSnippets = true;
        this.searchOpenedFlyout = true;
      } else if (!hasQuery && this.searchOpenedFlyout) {
        this.closeSnippets();
      }
    },
    'snippetData.categories': {
      immediate: true,
      handler(categories) {
        if (categories && categories.length > 0 && !this.selectedCategory) {
          const [firstCategory] = categories;
          this.selectedCategory = firstCategory;
        }
      },
    },
  },
  mounted() {
    this.refreshPanelLevel();
    window.addEventListener('message', this.handleCanvasMessage);
    // Emit initial sidebar width
    this.$nextTick(() => {
      this.emitCurrentWidth();
    });
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleCanvasMessage);
    this.clearHoverTimers();
  },
  methods: {
    normalizeSearchText(value) {
      const normalized = String(value || '').toLowerCase();
      if (typeof normalized.normalize === 'function') {
        // Make searches accent-insensitive (e.g. "uberschrift" matches "Ueberschrift/Überschrift").
        return normalized.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      }
      return normalized;
    },
    getSnippetSearchableText(snippet) {
      const tagText = Array.isArray(snippet.tags) ? snippet.tags.join(' ') : (snippet.tags || '');
      const parts = [
        snippet.titleDe,
        snippet.titleEn,
        snippet.title_de,
        snippet.title_en,
        snippet.title,
        snippet.name,
        snippet.tags_de,
        snippet.tags_en,
        tagText,
        (snippet.rollen || []).join(' '),
        snippet.beschreibung,
      ];
      return this.normalizeSearchText(parts.filter(Boolean).join(' '));
    },
    toggleSidebar() {
      this.isOpen = !this.isOpen;
      this.$emit('sidebar-toggle', this.isOpen);
    },
    hideSidebar() {
      this.isVisible = false;
      this.closeSnippets();
      this.$emit('sidebar-toggle', false);
      this.$emit('sidebar-width-change', 0);
    },
    showSidebar() {
      this.isVisible = true;
      this.$emit('sidebar-toggle', true);
      this.$nextTick(() => {
        this.emitCurrentWidth();
      });
    },
    refreshPanelLevel() {
      this.panelLevel = readLevel(this.userId);
    },
    /** Opens the flyout with this category (click, Enter, hover after the intent delay). */
    selectCategory(category) {
      this.clearHoverTimers();
      this.refreshPanelLevel();
      this.searchOpenedFlyout = false;
      this.searchQuery = '';
      this.selectedCategory = category;
      this.showSnippets = true;
    },
    closeSnippets() {
      this.clearHoverTimers();
      this.showSnippets = false;
      this.flyoutPinned = false;
      this.searchOpenedFlyout = false;
      this.searchQuery = '';
    },
    /** "+" between snippets: open the last category (or "Alle") and keep it open until a pick or close. */
    pinSnippets() {
      this.selectCategory(this.selectedCategory || 'All');
      this.flyoutPinned = true;
    },
    hoverCategory(category) {
      this.clearHoverTimers();
      if (this.showSnippets && !this.searchQuery && this.selectedCategory === category) return;
      this.hoverOpenTimer = setTimeout(() => this.selectCategory(category), HOVER_OPEN_MS);
    },
    leaveCategories() {
      clearTimeout(this.hoverOpenTimer);
      this.hoverOpenTimer = null;
      this.scheduleFlyoutClose();
    },
    keepFlyoutOpen() {
      this.clearHoverTimers();
    },
    scheduleFlyoutClose() {
      clearTimeout(this.hoverCloseTimer);
      this.hoverCloseTimer = null;
      // A pinned flyout, search results and a running drag (its overlay makes the pointer "leave") stay.
      if (!this.showSnippets || this.flyoutPinned || this.searchQuery || this.dragCandidate) return;
      this.hoverCloseTimer = setTimeout(() => this.closeSnippets(), HOVER_CLOSE_MS);
    },
    clearHoverTimers() {
      clearTimeout(this.hoverOpenTimer);
      clearTimeout(this.hoverCloseTimer);
      this.hoverOpenTimer = null;
      this.hoverCloseTimer = null;
    },
    handleCanvasMessage(event) {
      // A pinned flyout waits for a pick; selecting something in the canvas means the user moved on.
      const messageType = event && event.data ? event.data.type : null;
      if (this.flyoutPinned && messageType === 'ELEMENT_SELECTED') {
        this.closeSnippets();
      }
    },
    switchTab(tab) {
      this.activeTab = tab;
      if (tab === 'layout') {
        this.closeSnippets();
      }
      this.$nextTick(() => {
        this.emitCurrentWidth();
      });
    },
    selectLayout(style) {
      this.$emit('select-layout', style);
    },
    getLayoutPreviewStyle(style, side) {
      // Generate CSS for the layout preview visualization
      /* eslint-disable quote-props */
      const widthMap = {
        100: { left: '100%', right: '0%' },
        '70_30': { left: '70%', right: '30%' },
        '60_40': { left: '60%', right: '40%' },
        '50_50': { left: '50%', right: '50%' },
        '40_60': { left: '40%', right: '60%' },
        '30_70': { left: '30%', right: '70%' },
        '70_30_r': { left: '30%', right: '70%' },
        '60_40_r': { left: '40%', right: '60%' },
        '50_50_r': { left: '50%', right: '50%' },
        '40_60_r': { left: '60%', right: '40%' },
        '30_70_r': { left: '70%', right: '30%' },
        '100_30': { left: '100%', right: '30%' },
        '100_40': { left: '100%', right: '40%' },
        '100_50': { left: '100%', right: '50%' },
        '100_60': { left: '100%', right: '60%' },
        '100_70': { left: '100%', right: '70%' },
        '50_50s': { left: '50%', right: '50%' },
      };
      /* eslint-enable quote-props */
      const w = widthMap[style] || { left: '100%', right: '0%' };
      const isCentered = style.startsWith('100_');
      if (side === 'left') {
        return {
          width: w.left,
          background: isCentered ? '#e0e0e0' : '#6366f1',
          opacity: isCentered ? '0.5' : '1',
        };
      }
      if (style === '100') {
        return { width: '0%', display: 'none' };
      }
      return {
        width: isCentered ? w.right : w.right,
        background: isCentered ? '#6366f1' : '#a5b4fc',
        position: isCentered ? 'absolute' : 'relative',
        left: isCentered ? '50%' : 'auto',
        transform: isCentered ? 'translateX(-50%)' : 'none',
      };
    },
    emitCurrentWidth() {
      // One column, always the same width (see .categories-panel).
      this.$emit('sidebar-width-change', this.isVisible ? SIDEBAR_WIDTH : 0);
    },
    isV2Markup,
    containerSketch,
    /** Empty v2 containers and empty Bereiche (the layout rows) show a column sketch. */
    skizze(snippet) {
      return containerSketch(snippet.content || snippet.html) || bereichSketch(snippet.section);
    },
    getCategoryLabel(category) {
      if (category === FAVORITES_CATEGORY) {
        return this.$t('editor.sidebar.favorites');
      }
      return getSnippetCategoryLabel(category, this.language);
    },
    /** A UI word for a fixed data label (layout groups); unknown ones stay as they are. */
    uiLabel(list, key, fallback = key) {
      const path = `editor.sidebar.${list}.${key}`;
      return this.$te(path) ? this.$t(path) : fallback;
    },
    getSnippetTitle(snippet) {
      if (this.language === 'de' && snippet.title_de) {
        return snippet.title_de;
      }
      if (this.language === 'en' && snippet.title_en) {
        return snippet.title_en;
      }
      return snippet.title || snippet.name;
    },
    getPreviewUrl(preview) {
      if (!preview) return '';
      // Preview URLs in snippets are relative paths like "../../snippets_preview/_tennet/file.jpg"
      // The snippets_preview folder is inside the template collection folder
      // e.g., /wps/_modules/templates/global_collection2/snippets_preview/
      // Use templateBaseUrl prop to build absolute path
      if (preview.startsWith('../../')) {
        // Strip "../../" and prepend templateBaseUrl (which points to collection folder)
        const relativePath = preview.replace('../../', '');
        if (this.templateBaseUrl) {
          return `${this.templateBaseUrl}${relativePath}`;
        }
        // Fallback: assume global_collection2 if no templateBaseUrl provided
        return `/wps/_modules/templates/global_collection2/${relativePath}`;
      }
      if (preview.startsWith('../')) {
        const relativePath = preview.replace('../', '');
        if (this.templateBaseUrl) {
          return `${this.templateBaseUrl}${relativePath}`;
        }
        return `/wps/_modules/templates/global_collection2/${relativePath}`;
      }
      // DB snippet thumbs are stored as raw S3 URLs — serve them through the
      // Bunny CDN with a sidebar-sized variant (transform is a no-op for
      // non-S3 URLs).
      const bunnyUrl = this.$transformS3ToBunnyUrl(preview);
      if (bunnyUrl !== preview) {
        const sep = bunnyUrl.includes('?') ? '&' : '?';
        return `${bunnyUrl}${sep}width=400&quality=85`;
      }
      // If already absolute or different format, return as-is
      return preview;
    },
    isContainerSnippet(snippet) {
      if (!snippet) return false;
      if (snippet.type) {
        return snippet.type === 'container';
      }
      if (snippet.html) {
        return snippet.html.indexOf('data-wps-type="container"') !== -1;
      }
      return false;
    },
    handleImageError(e) {
      e.target.style.display = 'none';
      const sibling = e.target.nextElementSibling;
      if (sibling) {
        sibling.classList.remove('hidden');
      }
    },
    addSnippet(snippet) {
      // The pick ends the pinned mode; the flyout then closes like a hovered one.
      this.flyoutPinned = false;
      this.$emit('add-snippet', snippet);

      // Only dispatch keditor event in non-Vue3 mode (Vue3 mode uses the emit above)
      if (!this.isVue3Mode) {
        // Dispatch custom event to keditor
        window.dispatchEvent(new CustomEvent('keditor-command', {
          detail: {
            type: 'keditor-add-snippet',
            data: {
              snippetHtml: snippet.content || snippet.html,
              snippetName: snippet.name,
              forceRoot: true,
            },
          },
        }));
      }
    },
    openAiPasteModal() {
      // The Vue 3 canvas iframe owns the modal (the AI annotate flow lives in
      // aiSnippetState there). Locate the iframe by class — same pattern used
      // by the drag/drop code in this component.
      const iframe = document.querySelector('.vue3-canvas-iframe');
      if (!iframe || !iframe.contentWindow) {
        console.warn('[EditorSidebar] Cannot open AI paste modal: vue3-canvas-iframe not found');
        return;
      }
      iframe.contentWindow.postMessage(
        { type: 'OPEN_AI_PASTE_MODAL', payload: {} },
        '*',
      );
    },
    startSnippetDrag(snippet, event) {
      if (event.button !== 0) return;
      event.preventDefault();
      this.dragCandidate = {
        snippet,
        startX: event.clientX,
        startY: event.clientY,
      };
      this.isDraggingSnippet = false;
      this.createDragOverlay();
    },
    handleSnippetMouseMove(event) {
      if (!this.dragCandidate) return;
      const dx = event.clientX - this.dragCandidate.startX;
      const dy = event.clientY - this.dragCandidate.startY;
      if (!this.isDraggingSnippet && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
        this.isDraggingSnippet = true;
        this.dragSnippet = this.dragCandidate.snippet;
        this.dragSnippetIsContainer = this.isContainerSnippet(this.dragSnippet);
        this.createDragGhost(this.dragSnippet);
        // Emit drag start event for Vue 3 canvas
        this.$emit('snippet-drag-start', {
          ...this.dragSnippet,
          isContainer: this.dragSnippetIsContainer,
        });
      }
      if (this.isDraggingSnippet) {
        this.updateDragPosition(event);
      }
    },
    handleSnippetMouseUp() {
      const dragged = this.isDraggingSnippet;
      if (dragged) {
        this.dropSnippet();
      }
      this.endSnippetDrag();
      // After a real drag the flyout (hidden meanwhile) closes; a plain click leaves it open.
      if (dragged) {
        this.closeSnippets();
      }
    },
    createDragOverlay() {
      if (this.dragOverlay) return;
      const overlay = document.createElement('div');
      overlay.className = 'snippet-drag-overlay';
      this.dragMoveHandler = (event) => this.handleSnippetMouseMove(event);
      this.dragUpHandler = () => this.handleSnippetMouseUp();
      window.addEventListener('mousemove', this.dragMoveHandler);
      window.addEventListener('mouseup', this.dragUpHandler);
      document.body.appendChild(overlay);
      this.dragOverlay = overlay;
      this.attachIframeDragListeners();
    },
    attachIframeDragListeners() {
      // Find the appropriate iframe based on mode
      const iframeSelector = this.isVue3Mode
        ? '.vue3-canvas-iframe'
        : '.keditor-article iframe';
      const iframe = document.querySelector(iframeSelector);
      if (!iframe) {
        console.warn('[EditorSidebar] Iframe not found for drag:', iframeSelector);
        return;
      }
      // Always store iframe reference for position calculations
      this.dragIframe = iframe;

      // For same-origin iframes (keditor mode), attach event listeners to iframe document
      // For cross-origin iframes (Vue 3 mode), disable pointer-events on iframe so overlay captures events
      try {
        if (iframe.contentDocument) {
          this.dragIframeDoc = iframe.contentDocument;
          this.dragIframeMoveHandler = (event) => this.handleSnippetMouseMove(event);
          this.dragIframeUpHandler = () => this.handleSnippetMouseUp();
          this.dragIframeDoc.addEventListener('mousemove', this.dragIframeMoveHandler);
          this.dragIframeDoc.addEventListener('mouseup', this.dragIframeUpHandler);
        } else {
          // Cross-origin iframe - disable pointer-events so overlay can capture mouse events
          iframe.style.pointerEvents = 'none';
          this.iframePointerEventsDisabled = true;
        }
      } catch (e) {
        // Cross-origin iframe - disable pointer-events so overlay can capture mouse events
        iframe.style.pointerEvents = 'none';
        this.iframePointerEventsDisabled = true;
      }
    },
    createDragGhost(snippet) {
      if (this.dragGhost) return;
      const ghost = document.createElement('div');
      ghost.className = 'snippet-drag-ghost';
      ghost.style.cssText = [
        'position:fixed',
        'top:0',
        'left:0',
        'transform:translate(-9999px,-9999px)',
        'width:180px',
        'border-radius:10px',
        'background:#fff',
        'color:#1a1a1a',
        'border:1px solid rgba(0,0,0,0.08)',
        'box-shadow:0 10px 30px rgba(0,0,0,0.18)',
        'font-size:12px',
        'pointer-events:none',
        'z-index:2001',
        'overflow:hidden',
      ].join(';');
      const titleText = this.getSnippetTitle(snippet) || snippet.name || 'Snippet';
      const preview = document.createElement('div');
      preview.className = 'snippet-drag-ghost-preview';
      preview.style.cssText = [
        'width:100%',
        'height:80px',
        'background:#f2f2f2',
        'display:flex',
        'align-items:center',
        'justify-content:center',
      ].join(';');

      if (snippet && snippet.preview) {
        const img = document.createElement('img');
        img.src = this.getPreviewUrl(snippet.preview);
        img.alt = titleText;
        img.style.cssText = 'width:100%;height:100%;object-fit:cover;';
        preview.appendChild(img);
      } else {
        const placeholder = document.createElement('div');
        placeholder.className = 'snippet-drag-ghost-placeholder';
        placeholder.style.cssText = 'font-size:18px;color:#9a9a9a;';
        placeholder.textContent = '▦';
        preview.appendChild(placeholder);
      }

      const title = document.createElement('div');
      title.className = 'snippet-drag-ghost-title';
      title.style.cssText = 'padding:6px 8px 8px;font-weight:600;line-height:1.2;';
      title.textContent = titleText;

      ghost.appendChild(preview);
      ghost.appendChild(title);
      document.body.appendChild(ghost);
      this.dragGhost = ghost;
    },
    updateDragGhostPosition(clientX, clientY) {
      if (!this.dragGhost) return;
      this.dragGhost.style.transform = `translate3d(${clientX + 12}px, ${clientY + 12}px, 0)`;
    },
    updateDragPosition(event) {
      // Use the stored iframe reference or find the appropriate one based on mode
      const iframe = this.dragIframe || document.querySelector(
        this.isVue3Mode ? '.vue3-canvas-iframe' : '.keditor-article iframe',
      );
      if (!iframe) {
        console.warn('[EditorSidebar] updateDragPosition - no iframe found!');
        return;
      }
      const rect = iframe.getBoundingClientRect();
      const scaleX = iframe.clientWidth ? rect.width / iframe.clientWidth : 1;
      const scaleY = iframe.clientHeight ? rect.height / iframe.clientHeight : 1;
      const isIframeEvent = event.view === iframe.contentWindow
        || (event.target && event.target.ownerDocument === iframe.contentDocument);
      let x = 0;
      let y = 0;
      let inIframe = false;
      let parentX = event.clientX;
      let parentY = event.clientY;

      if (isIframeEvent) {
        x = event.clientX;
        y = event.clientY;
        inIframe = true;
        parentX = rect.left + (event.clientX * scaleX);
        parentY = rect.top + (event.clientY * scaleY);
      } else {
        inIframe = event.clientX >= rect.left
          && event.clientX <= rect.right
          && event.clientY >= rect.top
          && event.clientY <= rect.bottom;
        x = (event.clientX - rect.left) / scaleX;
        y = (event.clientY - rect.top) / scaleY;
      }
      this.lastDragPosition = { x, y, inIframe };
      this.updateDragGhostPosition(parentX, parentY);
      window.dispatchEvent(new CustomEvent('keditor-command', {
        detail: {
          type: 'keditor-snippet-drag-move',
          data: {
            x,
            y,
            inIframe,
            snippetIsContainer: this.dragSnippetIsContainer,
            snippetType: this.dragSnippet ? this.dragSnippet.type : null,
          },
        },
      }));
    },
    dropSnippet() {
      if (!this.dragSnippet || !this.lastDragPosition) return;
      if (this.lastDragPosition.inIframe) {
        // Dispatch drop event with position data for both Vue 3 and keditor modes
        window.dispatchEvent(new CustomEvent('keditor-command', {
          detail: {
            type: 'keditor-snippet-drag-drop',
            data: {
              x: this.lastDragPosition.x,
              y: this.lastDragPosition.y,
              snippetHtml: this.dragSnippet.content || this.dragSnippet.html,
              block: this.dragSnippet.block || null,
              snippetName: this.dragSnippet.name,
              snippetIsContainer: this.dragSnippetIsContainer,
              snippetType: this.dragSnippet ? this.dragSnippet.type : null,
            },
          },
        }));
        if (this.isVue3Mode) {
          this.$emit('snippet-drag-end');
        } else {
          window.dispatchEvent(new CustomEvent('keditor-command', {
            detail: { type: 'keditor-snippet-drag-end' },
          }));
        }
      } else if (!this.isVue3Mode) {
        // Dropped outside iframe - dispatch end event for keditor mode
        window.dispatchEvent(new CustomEvent('keditor-command', {
          detail: { type: 'keditor-snippet-drag-end' },
        }));
      }
    },
    endSnippetDrag() {
      if (this.dragOverlay) {
        if (this.dragMoveHandler) {
          window.removeEventListener('mousemove', this.dragMoveHandler);
        }
        if (this.dragUpHandler) {
          window.removeEventListener('mouseup', this.dragUpHandler);
        }
        this.dragOverlay.remove();
      }
      if (this.dragIframeDoc) {
        if (this.dragIframeMoveHandler) {
          this.dragIframeDoc.removeEventListener('mousemove', this.dragIframeMoveHandler);
        }
        if (this.dragIframeUpHandler) {
          this.dragIframeDoc.removeEventListener('mouseup', this.dragIframeUpHandler);
        }
      }
      // Re-enable pointer-events on iframe if we disabled them (cross-origin case)
      if (this.iframePointerEventsDisabled && this.dragIframe) {
        this.dragIframe.style.pointerEvents = '';
        this.iframePointerEventsDisabled = false;
      }
      if (this.dragGhost) {
        this.dragGhost.remove();
      }
      this.dragCandidate = null;
      this.dragOverlay = null;
      this.dragSnippet = null;
      this.dragSnippetIsContainer = false;
      this.isDraggingSnippet = false;
      this.lastDragPosition = null;
      this.dragMoveHandler = null;
      this.dragUpHandler = null;
      this.dragGhost = null;
      this.dragIframe = null;
      this.dragIframeDoc = null;
      this.dragIframeMoveHandler = null;
      this.dragIframeUpHandler = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.editor-sidebar-wrapper {
  position: fixed;
  right: 0;
  top: 60px;
  height: calc(100vh - 60px);
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  pointer-events: none;
}

.btn-show-sidebar {
  position: fixed;
  right: 20px;
  top: 90px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #51d182;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
  box-shadow: 0 2px 7px rgb(81 209 130 / 40%);
  transition: all 0.2s;
  pointer-events: auto;
  border:none;

  &:hover {
    background: #51d182;
    transform: scale(1.05);
  }
}

.editor-sidebar {
  display: flex;
  height: 100%;
  background: #ffffff;
  border-radius: 12px 0 0 12px;
  overflow: hidden;
  pointer-events: auto;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.categories-panel {
  width: 320px; // SIDEBAR_WIDTH
  min-width: 320px;
  display: flex;
  flex-direction: column;
  background: #fafafa;
  border-left: 1px solid #eaeaea;
}

// Second level: floats over the canvas left of the column, never widens it.
// Inside the wrapper's stacking context (z-index 1000): above the canvas, below modals.
.snippets-flyout {
  position: absolute;
  top: 0;
  right: 100%;
  width: 340px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #eaeaea;
  border-right: none;
  border-radius: 12px 0 0 12px;
  overflow: hidden;
  pointer-events: auto;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.14), 0 3px 8px rgba(0, 0, 0, 0.06);
}

.categories-header,
.snippet-icon { margin-right: 4px; vertical-align: -2px; }

.snippets-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px;
  border-bottom: 1px solid #eaeaea;
  background: #fff;

  .header-title {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a1a;
  }
}

.btn-add-ai-snippet {
  margin: 12px 16px 0;
  padding: 8px 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.9;
  }
}

.sidebar-tabs {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #eaeaea;
  background: #fff;

  .sidebar-tab {
    flex: 1;
    padding: 12px 16px;
    border: none;
    background: none;
    font-size: 14px;
    font-weight: 500;
    color: #9a9a9a;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.15s;

    &:hover {
      color: #4a4a4a;
    }

    &.active {
      color: #1a1a1a;
      border-bottom-color: #6366f1;
    }
  }

  .btn-hide-tabs {
    width: 28px;
    height: 28px;
    min-width: 28px;
    margin-right: 8px;
    border: none;
    background: #f0f0f0;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    font-size: 12px;
    transition: all 0.2s;

    &:hover {
      background: #e0e0e0;
      color: #333;
    }
  }
}

.category-search {
  position: relative;
  padding: 12px 16px;
  border-bottom: 1px solid #eaeaea;
  background: #fff;
}

.category-search-icon {
  position: absolute;
  left: 26px;
  top: 50%;
  transform: translateY(-50%);
  color: #9a9a9a;
  font-size: 13px;
}

.category-search-input {
  width: 100%;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 8px 28px 8px 30px;
  font-size: 13px;
  color: #1a1a1a;
  background: #f7f7f7;
  outline: none;

  &:focus {
    border-color: #6366f1;
    background: #fff;
  }
}

.category-search-clear {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #9a9a9a;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #1a1a1a;
  }
}

.btn-icon {
  width: 32px;
  height: 32px;
  border: none;
  background: #f0f0f0;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition: all 0.2s;

  &:hover {
    background: #e0e0e0;
    color: #333;
  }
}

.category-list {
  list-style: none;
  margin: 0;
  padding: 8px 0;
  overflow-y: auto;
  flex: 1;

  li {
    padding: 12px 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    color: #4a4a4a;
    transition: all 0.15s;
    border-right: 3px solid transparent;

    .chevron {
      opacity: 0.4;
      font-size: 12px;
      transition: all 0.15s;
    }

    &:hover {
      background: #f0f0f0;
      color: #1a1a1a;

      .chevron {
        opacity: 0.7;
        transform: translateX(-2px);
      }
    }

    &:focus-visible {
      outline: 2px solid #6366f1;
      outline-offset: -2px;
    }

    &.selected {
      background: #fff;
      color: #1a1a1a;
      font-weight: 500;
      border-right-color: #6366f1;

      .chevron {
        opacity: 1;
        color: #6366f1;
      }
    }
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.snippets-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #fff;
  animation: slideIn 0.2s ease-out;
}

.snippet-own {
  display: inline-block;
  margin-left: 4px;
  padding: 0 5px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4f46e5;
  font-size: 9px;
  font-weight: 600;
  vertical-align: middle;
}

.snippet-card {
  border: 1px solid #eaeaea;
  border-radius: 12px;
  position: relative;
  cursor: grab;
  transition: all 0.2s;
  background: #fff;

  &:hover {
    border-color: #6366f1;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
    cursor: grabbing;
  }

  &:focus-visible {
    outline: 2px solid #6366f1;
    outline-offset: 2px;
  }
}

.snippet-preview {
  width: 100%;
  min-height: 100px;
  background: #f9f9f9;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }
}

.snippet-placeholder {
  color: #ccc;
  font-size: 28px;
  padding: 30px;
}

.snippet-name {
  display: block;
  padding: 4px 8px;
  font-size: 11px;
  color: #888;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.snippet-add-button {
  position: absolute;
  // Above the live preview (.snippet-live is positioned and comes later): without it the "+" was covered and a
  // click on it hit the preview, so nothing was inserted (automation find 28.09., confirmed 29.09.2026).
  z-index: 1;
  top: 8px;
  right: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.95);
  color: #4a4a4a;
  border-radius: 6px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);

  &:hover {
    color: #1a1a1a;
    background: #fff;
  }
}

.snippet-favorite-button {
  position: absolute;
  z-index: 1;
  top: 8px;
  left: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.95);
  color: #4a4a4a;
  border-radius: 6px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  opacity: 0;
  transition: opacity 0.15s ease;

  &:hover {
    color: #1a1a1a;
    background: #fff;
  }

  &.is-favorite {
    color: #f5b301;
    opacity: 1;
  }
}

.snippet-card:hover .snippet-favorite-button {
  opacity: 1;
}

.snippet-drag-overlay {
  position: fixed;
  inset: 0;
  background: transparent;
  z-index: 2001;
  cursor: grabbing;
  /* pointer-events: auto is needed to capture mouse events over cross-origin iframes */
  pointer-events: auto;
}

.snippet-drag-ghost {
  position: fixed;
  top: 0;
  left: 0;
  transform: translate(-9999px, -9999px);
  width: 170px;
  border-radius: 10px;
  background: #fff;
  color: #1a1a1a;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  font-size: 12px;
  pointer-events: none;
  z-index: 2001;
  overflow: hidden;
}

.snippet-drag-ghost-preview {
  width: 100%;
  height: 80px;
  background: #f2f2f2;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.snippet-drag-ghost-placeholder {
  font-size: 18px;
  color: #9a9a9a;
}

.snippet-drag-ghost-title {
  padding: 6px 8px 8px;
  font-weight: 600;
  line-height: 1.2;
}

/* Layout Picker */
.layouts-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.layout-group {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.layout-group-label {
  font-size: 11px;
  font-weight: 600;
  color: #9a9a9a;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 10px;
}

.layout-group-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.layout-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px 6px 8px;
  border: 2px solid #eaeaea;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  background: #fff;

  &:hover {
    border-color: #c7c7c7;
    background: #fafafa;
  }

  &.active {
    border-color: #6366f1;
    background: #f5f3ff;
  }
}

.layout-preview {
  width: 100%;
  height: 32px;
  display: flex;
  gap: 2px;
  border-radius: 3px;
  overflow: hidden;
  position: relative;
}

.layout-preview-bar {
  height: 100%;
  border-radius: 2px;
  transition: width 0.2s;
}

.layout-label {
  font-size: 11px;
  color: #666;
  font-weight: 500;
  white-space: nowrap;
}

/* Scrollbar styling */
.category-list,
.snippets-list,
.layouts-list {
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d0d0d0;
    border-radius: 3px;

    &:hover {
      background: #b0b0b0;
    }
  }
}
</style>
